import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '../utils/toast';
import { Calendar, Users, TrendingUp, FileText, CreditCard, Search, Download, X, Check, ChevronRight, Info, Home, Warehouse, Building2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [pricingPlans, setPricingPlans] = useState(null);
  
  // Modal states
  const [planDetailsModalOpen, setPlanDetailsModalOpen] = useState(false);
  const [invoicesModalOpen, setInvoicesModalOpen] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [planDetailsLoading, setPlanDetailsLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      toast.error('Please sign in to access Subscription');
      navigate('/signin');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    if (parsedUser.role !== 'Hospital Admin') {
      toast.error('Only Hospital Admins can access subscription management');
      navigate('/dashboard');
      return;
    }
    
    fetchDashboardData();
    fetchPricingPlans();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If no subscription, redirect to plans page
      if (!response.data.has_subscription) {
        toast.info('No active subscription found. Please choose a plan.');
        navigate('/subscription/plans');
        return;
      }
      
      setDashboardData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch subscription data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingPlans = async () => {
    try {
      const response = await axios.get(`${API}/cms/pricing-plans`);
      setPricingPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fetch Plan Details for modal
  const fetchPlanDetails = async () => {
    try {
      setPlanDetailsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/plan-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch plan details');
    } finally {
      setPlanDetailsLoading(false);
    }
  };

  // Fetch Invoices for modal
  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Open Plan Details modal
  const openPlanDetailsModal = () => {
    setPlanDetailsModalOpen(true);
    fetchPlanDetails();
  };

  // Open Invoices modal
  const openInvoicesModal = () => {
    setInvoicesModalOpen(true);
    fetchInvoices();
  };

  // Handle invoice download
  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/subscriptions/invoices/${invoiceId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  // Handle receipt download
  const handleDownloadReceipt = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/subscriptions/invoices/${invoiceId}/receipt/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  // Filter invoices based on search term
  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoice_number?.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
    invoice.plan_name?.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
  );

  if (loading || !user || !dashboardData) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading subscription data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const { subscription, usage_percentage, remaining_licenses } = dashboardData;

  // Determine current plan type for roadmap
  const getCurrentPlanType = () => {
    const planName = subscription?.plan_name?.toLowerCase() || '';
    if (planName.includes('enterprise')) return 'enterprise';
    if (planName.includes('advanced')) return 'advanced';
    return 'core';
  };

  const currentPlanType = getCurrentPlanType();

  // Compact Plan Roadmap Component (inline version)
  const CompactPlanRoadmap = () => {
    const plans = [
      { type: 'core', name: 'Core', Icon: Warehouse },
      { type: 'advanced', name: 'Advanced', Icon: Home },
      { type: 'enterprise', name: 'Enterprise', Icon: Building2 }
    ];

    const getPlanFeatures = (planType) => {
      if (pricingPlans && pricingPlans[planType]) {
        return pricingPlans[planType].features || [];
      }
      return [];
    };

    return (
      <div className="flex items-center space-x-1 mt-3">
        {plans.map((plan, index) => {
          const isActive = plan.type === currentPlanType;
          const isPast = plans.findIndex(p => p.type === currentPlanType) > index;
          const features = getPlanFeatures(plan.type);
          const isLast = index === plans.length - 1;
          const PlanIcon = plan.Icon;

          return (
            <React.Fragment key={plan.type}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center cursor-pointer group">
                      <div 
                        className={`relative w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${
                          isActive 
                            ? 'bg-[#0056B3] text-white shadow-md ring-2 ring-blue-200' 
                            : isPast 
                              ? 'bg-[#0056B3]/70 text-white' 
                              : 'bg-gray-100 text-gray-500 border border-gray-300 group-hover:border-gray-400'
                        }`}
                      >
                        <PlanIcon className="w-4 h-4" />
                        {/* Tick overlay for active plan only */}
                        {isActive && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                          </span>
                        )}
                      </div>
                      <span className={`text-[10px] mt-1 whitespace-nowrap ${
                        isActive ? 'text-[#0056B3] font-semibold' : 'text-gray-500'
                      }`}>
                        {plan.name}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="bottom" 
                    className="max-w-xs p-3 bg-white border shadow-xl z-50"
                  >
                    <div className="space-y-2">
                      <p className="font-bold text-[#0056B3] text-sm">{plan.name} Plan</p>
                      {features.length > 0 ? (
                        <ul className="space-y-1 max-h-40 overflow-y-auto">
                          {features.map((feature, idx) => (
                            <li 
                              key={idx} 
                              className={`flex items-start text-xs ${
                                feature.is_included ? 'text-gray-700' : 'text-gray-400 line-through'
                              }`}
                            >
                              {feature.is_included ? (
                                <Check className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                              ) : (
                                <X className="w-3 h-3 text-gray-400 mr-1.5 mt-0.5 flex-shrink-0" />
                              )}
                              <span className={feature.is_bold ? 'font-semibold' : ''}>
                                {feature.text}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-500">Features loading...</p>
                      )}
                      {!isActive && (
                        <Button 
                          size="sm" 
                          className="w-full mt-2 bg-[#0056B3] hover:bg-[#004494] text-xs py-1"
                          onClick={() => navigate('/subscription/plans')}
                        >
                          {isPast ? 'View Plan' : 'Upgrade'}
                        </Button>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* Connector line */}
              {!isLast && (
                <div className={`w-6 h-0.5 ${
                  isPast || (isActive && index < plans.findIndex(p => p.type === currentPlanType)) 
                    ? 'bg-[#0056B3]' 
                    : 'bg-gray-200'
                } ${isPast ? 'bg-[#0056B3]' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-6">SUBSCRIPTION DASHBOARD</h1>
        
        {/* Current Plan Overview */}
        <div className="bg-white rounded-xl p-8 shadow-md mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="flex items-center space-x-3">
                <h2 className="text-[28px] font-bold" style={{ color: '#0056B3' }}>{subscription.plan_name}</h2>
                <Badge className={`${getStatusColor(subscription.status)} text-xs`}>
                  {subscription.status.toUpperCase()}
                </Badge>
              </div>
              {/* Compact Plan Roadmap */}
              <CompactPlanRoadmap />
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={() => navigate('/subscription/plans')}
                className="bg-[#00AEEF] hover:bg-[#00AEEF]/90"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Upgrade Plan
              </Button>
              <Button
                onClick={() => navigate('/subscription/renew')}
                variant="outline"
                className="border-[#0056B3] text-[#0056B3]"
              >
                Renew Plan
              </Button>
            </div>
          </div>

          {/* Plan Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Users className="w-8 h-8 text-[#0056B3]" />
                <div>
                  <p className="text-sm text-gray-600">Total Licenses</p>
                  <p className="text-[22px] font-bold" style={{ color: '#0056B3' }}>{subscription.total_licenses}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Users className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-sm text-gray-600">Used Licences</p>
                  <p className="text-[22px] font-bold text-green-600">{subscription.used_licenses}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Users className="w-8 h-8 text-gray-600" />
                <div>
                  <p className="text-sm text-gray-600">Remaining Licenses</p>
                  <p className="text-[22px] font-bold text-gray-600">{remaining_licenses}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Usage Speedometer */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold" style={{ color: '#0056B3' }}>License Utilization</h3>
              <span className="text-sm font-medium">{usage_percentage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-gradient-to-r from-[#0056B3] to-[#00AEEF] h-4 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(usage_percentage, 100)}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {subscription.used_licenses} of {subscription.total_licenses} licenses in use
            </p>
          </div>

          {/* Dates Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-[#0056B3] mt-1" />
              <div>
                <p className="text-sm text-gray-600">Activation Date</p>
                <p className="font-medium">{formatDate(subscription.activation_date)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-orange-600 mt-1" />
              <div>
                <p className="text-sm text-gray-600">Expiry Date</p>
                <p className="font-medium">{formatDate(subscription.expiry_date)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-green-600 mt-1" />
              <div>
                <p className="text-sm text-gray-600">Next Billing Date</p>
                <p className="font-medium">{formatDate(subscription.next_billing_date)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Concurrent Users Widget */}
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#0056B3]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Concurrent Users</h3>
                  <p className="text-sm text-gray-500">Number of staff who can access the system simultaneously</p>
                </div>
              </div>
              <span className="text-[22px] font-bold text-[#0056B3]">{subscription.concurrent_users || subscription.total_licenses}</span>
            </div>
          </Card>

          <Card
            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={openInvoicesModal}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Invoices & Receipts</h3>
                  <p className="text-sm text-gray-500">Access payment history</p>
                </div>
              </div>
              <Button variant="ghost" size="sm">View</Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Plan Details Modal */}
      <Dialog open={planDetailsModalOpen} onOpenChange={setPlanDetailsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-[22px] font-bold" style={{ color: '#0056B3' }}>Plan Details</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0">
          {planDetailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : planDetails ? (
            <div className="space-y-6">
              <h3 className="text-xl font-bold" style={{ color: '#0056B3' }}>{planDetails.plan_name}</h3>
              
              {/* Usage Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Users className="w-8 h-8 text-[#0056B3] mx-auto mb-2" />
                  <p className="text-2xl font-bold" style={{ color: '#0056B3' }}>
                    {planDetails.total_staff_allowed === 999999 ? '∞' : planDetails.total_staff_allowed}
                  </p>
                  <p className="text-xs text-gray-600">Total Staff Allowed</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Users className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-600">{planDetails.active_staff}</p>
                  <p className="text-xs text-gray-600">Used Licences</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Users className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-600">{planDetails.remaining_licenses}</p>
                  <p className="text-xs text-gray-600">Remaining Licenses</p>
                </div>
              </div>
              
              {/* Usage Bar */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold">License Usage</h4>
                  <span className="text-sm font-medium">
                    {((planDetails.active_staff / planDetails.total_staff_allowed) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-gradient-to-r from-[#0056B3] to-[#00AEEF] h-4 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((planDetails.active_staff / planDetails.total_staff_allowed) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Concurrent Users */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Users className="w-6 h-6 text-[#0056B3]" />
                  <div>
                    <p className="font-semibold">Concurrent Users Limit</p>
                    <p className="text-sm text-gray-600">{planDetails.concurrent_users} users can access simultaneously</p>
                  </div>
                </div>
              </div>
              
              {/* Features */}
              {planDetails.features && planDetails.features.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Plan Features</h4>
                  <ul className="space-y-2">
                    {planDetails.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No plan details available</p>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoices & Receipts Modal */}
      <Dialog open={invoicesModalOpen} onOpenChange={setInvoicesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-[22px] font-bold" style={{ color: '#0056B3' }}>Invoices & Receipts</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0">
          {invoicesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by invoice number or plan..."
                  value={invoiceSearchTerm}
                  onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Invoices Table */}
              {filteredInvoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                          <TableCell>{formatDate(invoice.created_at)}</TableCell>
                          <TableCell>{invoice.plan_name}</TableCell>
                          <TableCell>₹{invoice.amount?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge className={invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {invoice.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Invoice
                              </Button>
                              {invoice.status === 'paid' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadReceipt(invoice.id, invoice.invoice_number)}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Receipt
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No invoices found</p>
                </div>
              )}
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default SubscriptionDashboardPage;
